import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDownloadStats } from '../../common-showcase';

/**
 * PopularModulesCard — queue #662 §UAT-2026-07-09-R662 §R662-3 · `_index.md` §R662-cross.
 *
 * 대시보드 '인기 모듈' 카드 — common-showcase 가 public re-export 하는 `useDownloadStats()`
 * (`GET /api/eraf/catalog/download-stats` · 공유 쿼리키 `['download-stats']`) 의 `topModules`
 * 상위 5(queue #678 이후 복합점수 내림차순 — BE 정렬 그대로 소비, FE 재계산 0)를 렌더한다.
 * 각 항목에 타입별 내역(다운로드/담기/복사) 툴팁 + 보조 텍스트를 노출(§R678-4). 레이아웃은
 * `RecentModulesCard`(§R658) 카드 관례를 답습. 신규 endpoint/BE 0(read-only 소비) — 권한
 * 무관 전 역할 노출(학습 콘텐츠).
 * 5상태: default(상위 5) · loading(스켈레톤) · empty(안내 문구) · error(카드 전체 생략 —
 * fail-soft, 부가 지표라 대시보드 본체 무중단) · dialog(n/a).
 */
export function PopularModulesCard() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useDownloadStats();

  // error — 부가 지표 집계 실패 시 카드 통째로 생략(§R662-3 error 상태)
  if (isError) return null;

  const cardStyle: React.CSSProperties = {
    background: 'var(--color-surface-muted)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-lg)',
    padding: 'var(--spacing-4)',
    marginTop: 'var(--spacing-4)',
  };
  const headerRowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 'var(--spacing-3)',
  };
  const titleStyle: React.CSSProperties = {
    margin: 0,
    fontSize: 'var(--font-size-base)',
    fontWeight: 700,
    color: 'var(--color-text-primary)',
  };

  if (isLoading || !data) {
    return (
      <div aria-label={t('_index.popularModules.title')} style={cardStyle}>
        <div style={headerRowStyle}>
          <h2 style={titleStyle}>{t('_index.popularModules.title')}</h2>
        </div>
        <div
          role="status"
          aria-label={t('_index.popularModules.title')}
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}
        >
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              style={{
                height: 32,
                background: 'var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                animation: 'pulse 1.5s ease-in-out infinite',
                opacity: 1 - i * 0.15,
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  const topModules = data.topModules.slice(0, 5);

  return (
    <div aria-label={t('_index.popularModules.title')} style={cardStyle}>
      <div style={headerRowStyle}>
        <h2 style={titleStyle}>{t('_index.popularModules.title')}</h2>
        <Link
          to="/showcase"
          style={{
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-brand-primary)',
            textDecoration: 'none',
            fontWeight: 600,
          }}
        >
          {t('_index.popularModules.viewAll')}
        </Link>
      </div>

      {topModules.length === 0 ? (
        <p
          style={{
            margin: 0,
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-muted)',
            fontStyle: 'italic',
          }}
        >
          {t('_index.popularModules.empty')}
        </p>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-2)',
          }}
        >
          {topModules.map((mod) => {
            // queue #678 §R678-4: 정렬은 BE 복합점수(topModules 순서) 그대로 위임(FE 재계산 0).
            // 내역 툴팁 — 구 BE(#678 미배포) 응답엔 downloads/takes/copies 가 없을 수 있어
            // downloads 는 count 로 폴백, takes/copies 는 0 폴백(NON_NULL undefined 관례).
            const breakdown = t('_index.popularModules.breakdown', {
              downloads: mod.downloads ?? mod.count,
              takes: mod.takes ?? 0,
              copies: mod.copies ?? 0,
            });
            return (
              <li key={mod.code}>
                {/* #336 R12 hash 오토셀렉트 계보 — /showcase#<code> 딥링크 */}
                <Link
                  to={`/showcase#${mod.code}`}
                  title={breakdown}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    padding: 'var(--spacing-2) var(--spacing-3)',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--color-surface-border)',
                    background: 'var(--color-surface)',
                    color: 'var(--color-text-primary)',
                    textDecoration: 'none',
                    fontSize: 'var(--font-size-sm)',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontFamily: 'var(--font-family-mono)', color: 'var(--color-brand-primary)' }}>
                      {mod.code}
                    </span>
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                      {t('_index.popularModules.count', { count: mod.count })}
                    </span>
                  </span>
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                    {breakdown}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
